import { html, nothing } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { icons } from "../icons.ts";
import { toSanitizedMarkdownHtml } from "../markdown.ts";

/** Normalize gateway / formatter reasoning text for display. */
export function normalizeReasoningDisplayText(text: string): string {
  let trimmed = text.trim();
  if (!trimmed) {
    return "";
  }
  if (/^reasoning:/i.test(trimmed)) {
    trimmed = trimmed.replace(/^reasoning:\s*/i, "").trim();
  }
  return trimmed
    .split("\n")
    .map((line) => line.replace(/^_(.+)_$/, "$1").trim())
    .join("\n")
    .trim();
}

/** One-line muted preview for collapsed reasoning (Cursor-style). */
export function reasoningCollapsedPreview(text: string, maxLen = 120): string {
  const body = normalizeReasoningDisplayText(text);
  if (!body) {
    return "";
  }
  const line =
    body
      .split(/\n/)
      .find((l) => l.trim().length > 0)
      ?.trim() ?? body;
  if (line.length <= maxLen) {
    return line;
  }
  return `${line.slice(0, maxLen - 1)}…`;
}

export function renderReasoningBlock(params: {
  text: string;
  streaming?: boolean;
  defaultOpen?: boolean;
}) {
  const body = normalizeReasoningDisplayText(params.text);
  if (!body) {
    return nothing;
  }
  const open = params.streaming === true || params.defaultOpen === true;
  const preview = reasoningCollapsedPreview(body);
  return html`
    <details class="chat-reasoning" ?open=${open}>
      <summary class="chat-reasoning__summary">
        <span class="chat-reasoning__chevron" aria-hidden="true">▸</span>
        <span class="chat-reasoning__title">Reasoning</span>
        ${
          !open && preview
            ? html`<span class="chat-reasoning__preview muted">${preview}</span>`
            : nothing
        }
        ${
          params.streaming
            ? html`<span class="chat-reasoning__badge">${icons.loader} Thinking</span>`
            : nothing
        }
      </summary>
      <div class="chat-reasoning__body">
        <div class="chat-reasoning__content">
          ${unsafeHTML(toSanitizedMarkdownHtml(body))}
        </div>
      </div>
    </details>
  `;
}
